import React, { useEffect, useRef, useState, useCallback } from "react";
import axiosAuth from "../Axios/Interceptors";
import AppContext from "../Context/Context";
import PTDragDropUpload from "../DragDrop/PTDragDropUpload";
import {
  ConvertDateToETFormat,
  GetDifferenceBetweenTwoDatesInSeconds,
} from "../Shared/Utility";
import { useGlobalState } from "../Context/GlobalStateContext";
import { showErrorMessage } from "../Shared/Toast";

type Props = {
  OtherEntityID: number;
  MessageTypeID: number;
  AutoSaveMessage?: boolean;
  SendWAMessage?: boolean;
  MaxNumberOfFiles?: number;
  ShowCloseJob?: boolean;
  CloseThisJob?: Function;
  ShowMoveLeader?: number;
};

const Conversation: React.FC<Props> = ({
  OtherEntityID,
  MessageTypeID,
  AutoSaveMessage = false,
  SendWAMessage = false,
  MaxNumberOfFiles = 20,
  ShowCloseJob = false,
  CloseThisJob = Function,
  ShowMoveLeader = 0,
}) => {
  const globalState = useGlobalState();
  const [inputContent, setInputContent] = useState("");
  const [messages, setMessages] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [ptFilesList, setPTFilesList] = useState<any[]>([]);
  const [addAttachmentMode, setAddAttachmentMode] = useState(false);
  const [availableHeight, setAvailableHeight] = useState(window.innerHeight);

  const [showModal, setshowModal] = useState(false);
  const [ImageVideoURL, setImageVideoURL] = useState("");
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const cacheBuster = Date.now();

  const [moversList, setMoversList] = useState<any[]>([]);
  const [MoveLeaderMoverID, setMoveLeaderMoverID] = useState<any>(0);

  useEffect(() => {
    GetDriversOfThisJob(OtherEntityID);
    GetMNVQuoteOtherDataByMNVQuoteID(OtherEntityID);
    const resize = () => setAvailableHeight(window.innerHeight);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const loadMessages = useCallback(async () => {
    const res = await axiosAuth.post(
      AppContext.APIURL + "api/Conversation/AjaxLoadMessages",
      { OtherEntityID, MessageTypeID },
    );

    if (res.data?.[0]?.[0]?.Status === 1) {
      setMessages(res.data[1]);
      setAttachments(res.data[2]);
    }
  }, [OtherEntityID, MessageTypeID]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    updateRecordsTimeStamp();

    const interval = setInterval(() => {
      updateRecordsTimeStamp();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  function ResponseFromPTUploader(data: any) {
    setPTFilesList(data.PTFilesList);
  }

  const saveMessage = async () => {
    if (!inputContent && ptFilesList.length === 0 && !AutoSaveMessage) {
      showErrorMessage("Message is required");
      return;
    }
    const postData = {
      Content: inputContent,
      OtherEntityID,
      MessageTypeID,
      SendWAMessage,
      FilesData: ptFilesList,
    };

    setInputContent("");
    setPTFilesList([]);

    setAddAttachmentMode(false);
    await axiosAuth.post(
      AppContext.APIURL + "api/Conversation/AjaxSaveMessage",
      postData,
    );
    loadMessages();
  };

  const deleteMessage = async (messageID: number) => {
    await axiosAuth.post(AppContext.APIURL + "api/Conversation/DeleteMessage", {
      MessageID: messageID,
    });
    loadMessages();
  };

  const updateRecordsTimeStamp = () => {
    setMessages((prevMessages: any[]) =>
      prevMessages.map((msg) => {
        const date1 = new Date(msg.CreatedDate);
        const date2 = ConvertDateToETFormat(new Date());

        const seconds = GetDifferenceBetweenTwoDatesInSeconds(date1, date2);

        const minutes = Math.round(seconds / 60);
        const hours = Math.round(minutes / 60);
        const days = Math.round(hours / 24);

        let CreatedDateDisplay = "";

        if (seconds < 60) {
          CreatedDateDisplay = "< 1m";
        } else if (minutes < 60) {
          CreatedDateDisplay = `${minutes}m`;
        } else if (hours < 24) {
          CreatedDateDisplay = `${hours}h`;
        } else if (days < 7) {
          CreatedDateDisplay = `${days}d`;
        } else {
          CreatedDateDisplay =
            `${(date1.getMonth() + 1).toString().padStart(2, "0")}/` +
            `${date1.getDate().toString().padStart(2, "0")}/` +
            `${date1.getFullYear()}`;
        }

        return {
          ...msg,
          CreatedDateDisplay,
        };
      }),
    );
  };

  const openMedia = (url: string) => {
    setImageVideoURL(url);
    setshowModal(true);
  };

  const isVideoType = (url: string) =>
    url.toLowerCase().endsWith(".mp4") || url.toLowerCase().endsWith(".mov");

  const isAudioType = (path: string) =>
    ["mp3", "wav", "webm", "m4a", "ogg", "aac"].includes(
      path?.split(".").pop()?.toLowerCase() || "",
    );

  function ValidateAndCloseThisJob() {
    if (messages.length == 0) {
      showErrorMessage("Please add some feedback.");
    } else if (MoveLeaderMoverID == 0 || MoveLeaderMoverID == "") {
      showErrorMessage("Please select move leader.");
    } else {
      CloseThisJob();
    }
  }

  const GetDriversOfThisJob = (p_OtherEntityID: any) => {
    if(p_OtherEntityID > 0){
    const data = { QuoteID: p_OtherEntityID };
    axiosAuth
      .post(AppContext.APIURL + "api/MNVQuoteAdmin/MNVGetDriverOfThisJob", 
        data,
      )
      .then((res) => {
        const Movers = res.data[0];
        setMoversList(Movers);
      });
    }
  };

  const GetMNVQuoteOtherDataByMNVQuoteID = (MNVQuoteID: any) => {
    let data = {} as any;
    data.MNVQuoteID = MNVQuoteID;
    axiosAuth
      .post(
        AppContext.APIURL +
          "api/MNVQuoteAdmin/GetMNVQuoteOtherDataByMNVQuoteID",
        data,
      )
      .then((response) => {
        const QuoteData = response.data.Data;
        if (QuoteData && QuoteData[0] && QuoteData[0][0]) {
          setMoveLeaderMoverID(QuoteData[0][0]?.MoveLeaderMoverID);
        } else {
          setMoveLeaderMoverID(0);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  async function updateMoveLeaderMoverID(MoveLeaderMoverID: any) {
    try {
      const data: any = {
        MNVQuoteID: OtherEntityID,
        MoveLeaderMoverID: MoveLeaderMoverID,
      };
      const res = await axiosAuth.post(
        AppContext.APIURL + "api/MNVQuoteAdmin/updateMoveLeaderMoverID",
        data,
      );
    } catch (err) {
      console.error("updateBoxEsReturnData error:", err);
    } finally {
    }
  }

  return (
    <div className="mn-convo-1 font-mont">
      {ShowMoveLeader > 0 && (
        <div className="field-input flex-da flex-gap10 mb-10">
          <span className="font-12 text-left label-color">Move Leader:</span>
          <select
            disabled={ShowMoveLeader == 2}
            className="input-field"
            value={MoveLeaderMoverID}
            onChange={(e) => {
              setMoveLeaderMoverID(e.target.value);
              updateMoveLeaderMoverID(e.target.value);
            }}
          >
            <option value="0">Please Select</option>
            {moversList &&
              moversList.map((item: any, index: any) => (
                <React.Fragment key={index}>
                  <option value={item.MNVMoverID}>{item.Name}</option>
                </React.Fragment>
              ))}
          </select>
        </div>
      )}

      <div className="mn-convo-20">
        <textarea
          rows={4}
          value={inputContent}
          onChange={(e) => setInputContent(e.target.value)}
          placeholder="Type your message here ..."
        />
      </div>

      <div className="btn-start flex-da flex-jse mt-10 flex-gap10">
        <button className="flex-daj btn-primary" onClick={saveMessage}>
          Send
        </button>

        {!SendWAMessage && (
          <button
            className="flex-daj btn-primary"
            onClick={() => setAddAttachmentMode(!addAttachmentMode)}
          >
            Attachments
          </button>
        )}
      </div>

      {addAttachmentMode && (
        <div className="grid-d mt-20">
          <PTDragDropUpload
            MaxNumberOfFiles={MaxNumberOfFiles}
            UploadType={1}
            ObjectType={MessageTypeID}
            ObjectID={OtherEntityID}
            PTFilesList={[]}
            CanSendVoiceNotes={true}
            onResponse={ResponseFromPTUploader}
          />
        </div>
      )}

      <div className="mn-convo-3a mn-scroll-main">
        {messages.length === 0 && <h3>No messages to show</h3>}

        {messages.map((msg, i) => (
          <div
            key={i}
            className={`mn-convo-23 ${
              msg.SiteUserID !== globalState.LoggedInUserID
                ? "MessagingLeft"
                : "MessagingRight"
            }`}
          >
            <a className="mn-convo-24 c-theme">
              {msg.UserImageURL ? (
                <img src={msg.UserImageURL} alt="user" />
              ) : (
                <span>{msg.UserFullNameInitials}</span>
              )}
            </a>

            <div className="mn-convo-25">
              <div className="flex-da justify-btw">
                <span className="bold-font text-cap c-theme">
                  {msg.UserFullName}
                </span>
                <div className="flex-da flex-gap10">
                  <span className="font-12 c-theme">
                    {msg.CreatedDateDisplay}
                  </span>

                  {msg.SiteUserID == globalState.LoggedInUserID && (
                    <a onClick={() => deleteMessage(msg.MessageID)}>
                      <img src="/images/delete.png" width={14} height={14} />
                    </a>
                  )}
                </div>
              </div>

              <span className="mn-convo-29">{msg.MessageContents}</span>

              <ul className="mn-convo-30">
                {attachments
                  .filter((a) => a.MessageID === msg.MessageID)
                  .map((att, j) => (
                    <li key={j}>
                      <a onClick={() => openMedia(att.FileURLEncoded)}>
                        <img
                          width={100}
                          height={100}
                          src={AppContext.GetFileIconToDisplay(
                            att.FileURLEncoded,
                          )}
                        />
                      </a>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {ShowCloseJob == true && (
        <div className="flex-daj btn-start mt--15">
          <a
            className="btn-primary flex-daj"
            onClick={() => ValidateAndCloseThisJob()}
          >
            Close Job
          </a>
        </div>
      )}

      {showModal && (
        <div className="popup-help popup-help-index">
          <div className="help-popup-display">
            <div className="help-inner lg-popup">
              <div className="help-head flex-da justify-btw">
                <h3 />
                <a className="close-help" onClick={() => setshowModal(false)}>
                  <img src="/images/close.svg" width="15" height="15" />
                </a>
              </div>

              {ImageVideoURL && (
                <div className="grid-d flex-gap10">
                  {isVideoType(ImageVideoURL) ? (
                    <video ref={videoRef} controls width="700">
                      <source src={`${ImageVideoURL}?v=${cacheBuster}`} />
                    </video>
                  ) : isAudioType(ImageVideoURL) ? (
                    <audio controls style={{ width: "700px" }}>
                      <source src={`${ImageVideoURL}?v=${cacheBuster}`} />
                      Your browser does not support the audio element.
                    </audio>
                  ) : (
                    <img src={ImageVideoURL} width="700" />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Conversation;
